/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { flatten, isNil, mapKeys, max, toLower } from 'lodash';

export interface FormulaVariable {
  id: ID;
  name: string;
}

export type ActivatableFormulaVariable = FormulaVariable & {
  active?: boolean;
};

export interface FormulaFunction {
  name: string;
  allowedArgCount: number[];
  maxArgCount: number;
}

export interface FormulaNameResolver {
  resolveVariable(name: string): Maybe<FormulaVariable>;

  resolveFunction(name: string): Maybe<FormulaFunction>;
}

export interface StaticFormulaNameResolverOptions {
  variables: Hash<{ id: ID }>;
  functions: Hash<{ argCount: number | number[] }>;
}

export function isFormulaVariableActive(variable: ActivatableFormulaVariable): boolean {
  return variable.active || false;
}

const normalizeName = (_: any, key: string) => toLower(key);

export class StaticFormulaNameResolver implements FormulaNameResolver {
  private variables: Hash<{ id: ID }>;
  private functions: Hash<{ argCount: number | number[] }>;

  constructor({ variables, functions }: StaticFormulaNameResolverOptions) {
    this.variables = mapKeys(variables, normalizeName);
    this.functions = mapKeys(functions, normalizeName);
  }

  resolveVariable(name: string): Maybe<FormulaVariable> {
    const variable = this.variables[toLower(name)];
    return variable ? { id: variable.id, name } : void 0;
  }

  resolveFunction(name: string): Maybe<FormulaFunction> {
    const func = this.functions[toLower(name)];
    if (isNil(func)) {
      return void 0;
    }

    const allowedArgCount = flatten([func.argCount]);
    return func ? { name, allowedArgCount, maxArgCount: <number>max(allowedArgCount) } : void 0;
  }

  setVariables(variables: Hash<{ id: ID }>): void {
    this.variables = mapKeys(variables, normalizeName);
  }

  setFunctions(functions: Hash<{ argCount: number | number[] }>): void {
    this.functions = mapKeys(functions, normalizeName);
  }
}
